// frontend/admin-portal/src/components/products/ProductStockAdjustModal.jsx
import React, { useState, useEffect } from "react";
import { Modal, Button, Input, Alert } from "@fiberhome/shared-ui";
import { productService } from "../../services/productService";

export default function ProductStockAdjustModal({
  open,
  product,
  onClose,
  onSaved,
}) {
  const [mode, setMode] = useState("delta"); // 'delta' | 'set'
  const [delta, setDelta] = useState(0);
  const [setValue, setSetValue] = useState(0);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open && product) {
      setMode("delta");
      setDelta(0);
      setSetValue(Number(product.stock) || 0);
      setReason("");
      setError("");
    }
  }, [open, product]);

  const current = Number(product?.stock) || 0;
  const preview = mode === "delta" ? current + Number(delta) : Number(setValue);

  const handleSubmit = async () => {
    setSaving(true);
    setError("");
    try {
      const payload =
        mode === "delta"
          ? { delta: Number(delta), reason }
          : { set: Number(setValue), reason };
      await productService.adjustStock(product.id, payload);
      onSaved?.();
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Adjust Stock"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} loading={saving}>
            Apply
          </Button>
        </>
      }
    >
      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      <div className="mb-3">
        <div className="text-muted" style={{ fontSize: 12 }}>
          Product
        </div>
        <div style={{ fontWeight: 600 }}>{product?.name}</div>
        <div className="text-muted" style={{ fontSize: 12 }}>
          {product?.sku}
        </div>
      </div>

      <div className="d-flex gap-2 mb-3">
        <Button
          size="sm"
          variant={mode === "delta" ? "primary" : "ghost"}
          onClick={() => setMode("delta")}
        >
          Adjust by
        </Button>
        <Button
          size="sm"
          variant={mode === "set" ? "primary" : "ghost"}
          onClick={() => setMode("set")}
        >
          Set to
        </Button>
      </div>

      {mode === "delta" ? (
        <>
          <Input
            label="Delta (use negative to remove)"
            type="number"
            value={delta}
            onChange={(e) => setDelta(e.target.value)}
          />
          <div className="d-flex gap-2 mb-3">
            <Button size="sm" variant="ghost" onClick={() => setDelta(-1)}>
              -1
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setDelta(1)}>
              +1
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setDelta(-10)}>
              -10
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setDelta(10)}>
              +10
            </Button>
          </div>
        </>
      ) : (
        <Input
          label="New stock value"
          type="number"
          value={setValue}
          onChange={(e) => setSetValue(e.target.value)}
        />
      )}

      <Input
        label="Reason (optional)"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="e.g. Stock count, damaged, returned"
      />

      <Alert variant={preview < 0 ? "danger" : "info"} className="mb-0">
        Stock will go from <strong>{current}</strong> to{" "}
        <strong>{preview}</strong>
      </Alert>
    </Modal>
  );
}
